import ModalComponent from "components/Common/Modal";
import Render from "components/Common/Render";

import BusinessHeader from "components/Home/BusinessHeader";
import PromotionPlans from "components/Home/PromotionPlans";
import TopupDetails from "components/Home/TopupDetails";
import {
  rewardsSummary,
  businessPlans,
  businessOverallStatsApi,
  userFeedbackApi,
} from "constants/apiUrls";
import {
  FETCH_STATES,
  PlanActivationStatus,
  PreSignupStatusEnum,
  StripeSessionPaymentStatus,
} from "enum/common";
import { useLocalStorage } from "hooks/useLocalStorage";
import { http } from "middleware/apiMiddleware";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BusinessStats from "components/Home/BusinessStats";
import { RootState } from "store";
import { useAppSelector } from "hooks/useRedux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useLazyBusinessQuery } from "store/apis/businessApi";
import DeactivationConfirmationModal from "components/Home/DeactivationConfirmationModal";
import { setBusiness } from "store/slices/businessSlice";
import { useDispatch } from "react-redux";
import { checkIsAnywhereCommunity } from "utils/common";
import UserFeedbackTiles from "components/Home/UserFeedbackTiles";
import TransferConfirmationModal from "components/Home/TransferConfirmationModal";
import { Loader } from "components/Common/Loader";
import PaymentSucecssModal from "components/Home/PaymentSucecssModal";
import { usePusher } from "hooks/usePusher";
import useMixpanel from "hooks/useMixpanel";
import { routes } from "constants/routes";
import { useLazyDefaultAccountQuery } from "store/apis/defaultAccountApi";
import { setDefaultAccount } from "store/slices/defaultAccountSlice";
import PreRegisterModal from "components/Home/Preregister/PreRegisterModal";
import PreRegisterStatusReason from "components/Home/Preregister/PreRegisterStatusReason";
import BusinessDeactivationReason from "components/Home/BusinessDeactivationReason";
import ActivateOfferModal from "components/Home/ActivateOfferModal";
import PositiveFeedback from "components/Home/PositiveFeedback/PositiveFeedback";

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldShowPreSignup, setShouldShowPreSignup] =
    useState<boolean>(false);
  const [isPreRegisterationSuccessfull, setIsPreRegisterationSuccessfull] =
    useState(false);
  const [activateOfferModal, setActivateOfferModal] =
    useState<ActivateOfferModalType>(null);
  const isPaymentSucecss = useMemo(
    () =>
      searchParams.get("paymentStatus") === StripeSessionPaymentStatus.SUCCESS,
    [searchParams]
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackEvent } = useMixpanel();

  const [fetchBusienss, { isFetching }] = useLazyBusinessQuery();
  const [
    fetchDefaultAccount,
    { data: defaultPaymentMethod, isSuccess: isAccountsetupSuccess },
  ] = useLazyDefaultAccountQuery({});

  const [errorMessage, setErrorMessage] = useState("");
  const [updatingAmount, setUpdatingAmount] = useState(false);
  const [deActivatingOffer, setDeactivatingOffer] = useState(false);
  const [transferringAmount, setTransferringAmount] = useState(false);
  const [silentUpdate, setSilentUpdate] = useState(false);
  const [preSignupHeader, setPreSignupHeader] = useState("Pre-registration");
  const [showDeactiveConfirmDialog, setShowDeactiveConfirmDialog] = useState<{
    moduleId: string;
  } | null>(null);
  const [businessStats, setBusinessStats] = useState<{
    status: FETCH_STATES;
    data: BusinessStatsType;
  }>({ status: FETCH_STATES.LOADING, data: null });
  const [plans, setPlans] = useState<{
    status: FETCH_STATES;
    data: BusinessPlanType[];
  }>({ status: FETCH_STATES.LOADING, data: [] });
  const [businessOverallStats, setBusinessOverallStats] = useState<{
    status: FETCH_STATES;
    data: BusinessOverallStatsType | null;
  }>({ status: FETCH_STATES.LOADING, data: null });
  const [userFeedback, setUserFeedback] = useState<{
    status: FETCH_STATES;
    data: Array<UserFeedbackType> | null;
  }>({ status: FETCH_STATES.LOADING, data: null });
  const [
    showTransferBalanceConfirmDialog,
    setShowTransferBalanceConfirmDialog,
  ] = useState<{
    transferAmount: number;
  } | null>(null);

  const [selectedBusiness, setSelectedBusiness] = useLocalStorage(
    "selectedBusiness",
    null
  );
  const businessState = useAppSelector((state: RootState) => state.businesses);
  const business: BusinessType =
    businessState?.business || selectedBusiness || null;
  const preSignupStatus = useMemo(
    () => business?.preSignupDetails?.preSignupStatus,
    [business?.preSignupDetails?.preSignupStatus]
  );

  const isAnyBusinessActivity =
    !!businessStats?.data?.totalRedeemAmount ||
    !!businessStats?.data?.totalRewardsDistributed ||
    !!businessStats?.data?.totalTransactionAmount;

  const isAnyCommunityActivity =
    !!businessOverallStats?.data?.totalUsersCount ||
    !!businessOverallStats?.data?.totalAmountSpent ||
    !!businessOverallStats?.data?.totalBusinessesCount;

  const removeSearchParams = () => {
    if (searchParams.has("paymentStatus")) {
      searchParams.delete("paymentStatus");
      setSearchParams(searchParams);
    }
  };

  const onPaymentSucecssModalClose = () => {
    removeSearchParams();
  };

  const onPreRegisterClick = () => {
    setIsPreRegisterationSuccessfull(false);
    setShouldShowPreSignup(true);
  };
  const onLearnMoreClick = () => {
    setIsPreRegisterationSuccessfull(false);
    setShouldShowPreSignup(true);
  };

  const fetchBusinessStats = async (businessUuid: string) => {
    try {
      setBusinessStats({ status: FETCH_STATES.LOADING, data: null });
      const { data, error } = await http.get(rewardsSummary(businessUuid));
      if (error) {
        setBusinessStats({ status: FETCH_STATES.FAILED, data: null });
      }
      setBusinessStats({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setBusinessStats({ status: FETCH_STATES.FAILED, data: null });
      console.error("Error occurred while fetching business stats");
    }
  };

  const fetchPromotionPlans = async (businessUuid: string) => {
    try {
      setPlans({ status: FETCH_STATES.LOADING, data: [] });
      const { data, error } = await http.get(businessPlans(businessUuid));
      if (error) {
        setPlans({ status: FETCH_STATES.FAILED, data: [] });
      }
      setPlans({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setPlans({ status: FETCH_STATES.FAILED, data: [] });
      console.error("Error occurred while fetching promotion plans");
    }
  };

  const fetchBusinessOverallStats = async () => {
    try {
      setBusinessOverallStats({ status: FETCH_STATES.LOADING, data: null });
      const { data, error } = await http.get(
        businessOverallStatsApi(business?.uuid)
      );
      if (error) {
        setBusinessOverallStats({ status: FETCH_STATES.FAILED, data: null });
      }
      setBusinessOverallStats({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setBusinessOverallStats({ status: FETCH_STATES.FAILED, data: null });
      console.error("Error occurred while fetching user businessOverallStats");
    }
  };

  const fetchUserFeedbacks = async () => {
    try {
      setUserFeedback({ status: FETCH_STATES.LOADING, data: null });
      const { data, error } = await http.get(userFeedbackApi(business?.uuid));
      if (error) {
        setUserFeedback({ status: FETCH_STATES.FAILED, data: null });
      }
      setUserFeedback({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setUserFeedback({ status: FETCH_STATES.FAILED, data: null });
      console.error("Error occurred while fetching user businessOverallStats");
    }
  };

  const activateOffer = (
    moduleId: string,
    topUpAmount: number,
    referralCode: string
  ) => {
    // Uncomment to use old stripe handled flow
    // navigate(routes.purchase, {
    //   state: {
    //     moduleId,
    //     topUpAmount,
    //     businessId: business?.uuid,
    //     referralCode,
    //   },
    // });
    setActivateOfferModal({
      moduleId,
      topupAmount: topUpAmount,
      businessId: business?.uuid,
      referralCode,
    });
  };

  const onDeactivationConfig = async () => {
    try {
      setDeactivatingOffer(true);
      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${showDeactiveConfirmDialog?.moduleId}`,
        {
          enabled: false,
        }
      );
      if (!!data && !error) toast.success("Offer deactivated successfully");
      trackEvent("deactivate_offer_success", {
        planId: showDeactiveConfirmDialog?.moduleId,
      });
      setShowDeactiveConfirmDialog(null);
      setBusinessDetails();
    } catch (error) {
      trackEvent("deactivate_offer_failure", {
        planId: showDeactiveConfirmDialog?.moduleId,
      });
      setShowDeactiveConfirmDialog(null);
      toast.error("Unexpected error occurred while deactivating plan");
    } finally {
      setShowDeactiveConfirmDialog(null);
      setDeactivatingOffer(false);
    }
  };

  const deActivateOffer = async (moduleId: string) => {
    trackEvent("deactivate_offer_button_Clicking", { planId: moduleId });
    setShowDeactiveConfirmDialog({ moduleId });
  };

  const onTransferConfirm = async () => {
    try {
      setTransferringAmount(true);
      const { data, error } = await http.post(
        `/business/${business?.uuid}/transfer-funds`,
        {
          enabled: true,
          chargeAmount: businessStats?.data?.walletBalance,
        }
      );
      if (!!data && !error) {
        toast.success("Transfer balance request raised successfully");
        setBusinessDetails();
      }
    } catch (error) {
      toast.error("Unexpected error occurred while raising transfer request");
    } finally {
      setShowTransferBalanceConfirmDialog(null);
      setTransferringAmount(false);
    }
  };

  const updateTopupAmount = async (moduleId: string, topUpAmount: number) => {
    try {
      setUpdatingAmount(true);
      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${moduleId}`,
        {
          enabled: true,
          formData: { autoTopUpAmount: topUpAmount },
        }
      );
      if (!!data && !error) {
        toast.success("Topup amount updated successfully");
        trackEvent("update_topup_amount_success", {
          planId: moduleId,
          topUpAmount,
        });
      }
    } catch (error) {
      trackEvent("update_topup_amount_failure", {
        planId: moduleId,
        topUpAmount,
        error,
      });
      toast.error("Unexpected error occurred while updating topup amount");
    } finally {
      setUpdatingAmount(false);
    }
  };

  const setBusinessDetails = async () => {
    try {
      let updatedData: any = business;
      const { data, isError, error } = await fetchBusienss({
        uuid: business?.uuid,
      });
      // @ts-ignore
      const errorStatus = error?.originalStatus;

      if (isError && errorStatus == 404) {
        console.warn("Business not found");
        navigate(`${routes.chooseBusiness}/1`);
        return;
      }

      updatedData = data;
      if (updatedData) {
        dispatch(setBusiness({ ...updatedData }));
        setSelectedBusiness(updatedData);
        fetchBusinessStats(business?.uuid);
        fetchPromotionPlans(business?.uuid);
        fetchBusinessOverallStats();
        fetchUserFeedbacks();
      }
    } catch (error) {
      console.error("error while fetching business details");
    } finally {
      setSilentUpdate(false);
    }
  };

  const getCommunityDetails = useCallback(() => {
    const businessCommunities = business?.communities;
    if (!businessCommunities?.length) {
      return business?.onboardingCommunityDetails;
    }
    if (businessCommunities?.length === 1) {
      return businessCommunities[0];
    }
    if (businessCommunities?.length > 1) {
      const filteredAnyWhereCommunityList = businessCommunities?.filter(
        (community) => community?.isGlobal
      );

      return !!filteredAnyWhereCommunityList?.length
        ? filteredAnyWhereCommunityList[0]
        : businessCommunities[0];
    }

    return businessCommunities[0];
  }, [business?.communities]);

  const communityDetails = getCommunityDetails();

  const shouldShowPreRegisterBanner =
    communityDetails?.preSignupEnabled &&
    business.preSignupDetails.preSignupStatus !== PreSignupStatusEnum.PROCESSED;

  const isAnyWhereCommunity = checkIsAnywhereCommunity(business);

  const onCancelPreregisterModal = () => {
    setShouldShowPreSignup(false);
    setPreSignupHeader("Pre-registration");
  };

  const onCancelActivateOfferModal = () => {
    setActivateOfferModal(null);
    setBusinessDetails();
  };

  const onCloseModal = () => {
    setActivateOfferModal(null);
  };

  useEffect(() => {
    if (business) {
      fetchBusinessStats(business?.uuid);
      fetchPromotionPlans(business?.uuid);
      fetchBusinessOverallStats();
      fetchUserFeedbacks();

      fetchDefaultAccount({ uuid: business?.uuid });
    } else {
      navigate(`${routes.chooseBusiness}/1`);
    }
  }, [business?.uuid]);

  useEffect(() => {
    setBusinessDetails();
  }, []);

  useEffect(() => {
    if (isAccountsetupSuccess && defaultPaymentMethod) {
      dispatch(setDefaultAccount(defaultPaymentMethod));
    }
  }, [defaultPaymentMethod, isAccountsetupSuccess]);

  const onBusinessPusher = () => {
    setBusinessDetails();
  };
  const onTransactionUpdate = () => {
    setSilentUpdate(true);
    fetchBusinessStats(business?.uuid);
  };

  usePusher(
    [
      {
        eventName: "REACTIVATED",
        eventCallback: onBusinessPusher,
      },
    ],
    `business-${business?.uuid}`,
    [business?.uuid]
  );

  usePusher(
    [
      {
        eventName: "ACTIVATED",
        eventCallback: onBusinessPusher,
      },
    ],
    `business-${business?.uuid}`,
    [business?.uuid]
  );

  usePusher(
    [
      {
        eventName: "TRANSACTION_UPDATED",
        eventCallback: onTransactionUpdate,
      },
    ],
    `business-${business?.uuid}`,
    [business?.uuid]
  );

  const isLoading = useMemo(
    () =>
      !silentUpdate &&
      (businessStats?.status === FETCH_STATES.LOADING ||
        plans?.status === FETCH_STATES.LOADING ||
        businessOverallStats?.status === FETCH_STATES.LOADING ||
        userFeedback?.status === FETCH_STATES.LOADING ||
        isFetching),
    [
      silentUpdate,
      businessStats?.status,
      plans?.status,
      businessOverallStats?.status,
      userFeedback?.status,
    ]
  );
  useEffect(() => {
    if (business) {
      const showPreRegisterPopup =
        communityDetails?.preSignupEnabled &&
        !(
          preSignupStatus === PreSignupStatusEnum.ENROLLED ||
          preSignupStatus === PreSignupStatusEnum.PROCESSED
        );
      if (!isLoading) {
        setShouldShowPreSignup(showPreRegisterPopup);
      }
    }
  }, [business, isLoading]);

  return (
    <>
      <Render when={shouldShowPreRegisterBanner}>
        <PreRegisterStatusReason
          onPreRegisterClick={onPreRegisterClick}
          onLearnMoreClick={onLearnMoreClick}
          selectedBusiness={business}
          cityName={communityDetails?.cityName}
        />
      </Render>
      <Render
        when={business?.businessStatusMessage && !shouldShowPreRegisterBanner}
      >
        <BusinessDeactivationReason business={business} />
      </Render>
      <div
        className={`bg-offWhite p-4 mx-auto md:w-[65vw] h-full flex-col flex gap-8 `}
      >
        <Render when={!!business}>
          <BusinessHeader selectedBusiness={business} />
          <Render when={isLoading}>
            <div className="flex justify-center items-center h-screen">
              <Loader size={8} color={"darkBlue"} />
            </div>
          </Render>
          <Render when={!isLoading}>
            <>
              <div
                className={`md:flex-col gap-4 md:overflow-hidden overflow-scroll flex ${
                  plans?.data[0]?.status === PlanActivationStatus.ACTIVATED
                    ? "flex-row gap-4"
                    : "flex-col gap-4"
                }gap-4 no-scrollbar`}
              >
                <Render
                  when={
                    !!businessOverallStats?.data?.userFeedbacks ||
                    !!businessOverallStats?.data?.positiveInfluence
                  }
                >
                  <UserFeedbackTiles
                    businessOverallStats={businessOverallStats?.data}
                    isPlanActive={
                      plans?.data[0]?.status === PlanActivationStatus.ACTIVATED
                    }
                  />
                  <Render when={!!userFeedback?.data?.length}>
                    <PositiveFeedback userFeedback={userFeedback?.data} />
                  </Render>
                </Render>
              </div>

              {isAnyWhereCommunity ? (
                <>
                  <Render
                    when={
                      !!business &&
                      businessStats?.status === FETCH_STATES.SUCECSS
                    }
                  >
                    <Render when={isAnyBusinessActivity}>
                      <BusinessStats businessStats={businessStats?.data} />
                    </Render>
                    <Render
                      when={!!businessStats?.data?.entityTransactionsCount}
                    >
                      <TopupDetails
                        setShowTransferDialog={(transferAmount: number) =>
                          setShowTransferBalanceConfirmDialog({
                            transferAmount,
                          })
                        }
                        businessStats={businessStats?.data}
                        business={business}
                        plans={plans?.data}
                        // loading={businessStats?.status === FETCH_STATES.LOADING}
                        loading={true}
                      />
                    </Render>
                  </Render>
                  <Render when={businessStats?.status === FETCH_STATES.FAILED}>
                    <div className="text-orange">
                      Unable to fetch Business Stats
                    </div>
                  </Render>
                </>
              ) : (
                <>
                  <Render when={isAnyBusinessActivity}>
                    <BusinessStats businessStats={businessStats?.data} />
                  </Render>
                </>
              )}
              <PromotionPlans
                activateOffer={activateOffer}
                deActivateOffer={deActivateOffer}
                plans={plans?.data}
                updateTopupAmount={updateTopupAmount}
                isUpdatingAmount={updatingAmount}
                isAnyWhereCommunity={isAnyWhereCommunity}
                business={business}
                errorMessage={errorMessage}
                setErrorMessage={(value) => {
                  setErrorMessage(value);
                }}
              />
            </>
          </Render>

          <Render when={!!showDeactiveConfirmDialog}>
            <ModalComponent
              containerClass="md:w-[520px] md:h-[300px] h-[320px] w-[320px]"
              onClose={() => setShowDeactiveConfirmDialog(null)}
            >
              <DeactivationConfirmationModal
                onDeActivate={onDeactivationConfig}
                onCancel={() => setShowDeactiveConfirmDialog(null)}
                isDeactivatingOffer={deActivatingOffer}
              />
            </ModalComponent>
          </Render>
          <Render when={!!showTransferBalanceConfirmDialog}>
            <ModalComponent
              containerClass="md:w-[460px] md:h-[320px] h-[340px] w-[320px]"
              onClose={() => setShowTransferBalanceConfirmDialog(null)}
            >
              <TransferConfirmationModal
                onTransfer={onTransferConfirm}
                onCancel={() => setShowTransferBalanceConfirmDialog(null)}
                transferAmount={businessStats?.data?.walletBalance}
                transferringAmount={transferringAmount}
              />
            </ModalComponent>
          </Render>
          <Render when={shouldShowPreSignup && !isPreRegisterationSuccessfull}>
            <ModalComponent
              showClose
              containerClass="md:w-[50vw] md:h-auto h-auto  max-h-[80vh] w-[80vw] !md:m-0 !p-0 !z-50"
              onClose={onCancelPreregisterModal}
              renderHeader={
                <div className="text-darkBlue md:text-xl text-base font-medium">
                  {preSignupHeader}
                </div>
              }
            >
              <PreRegisterModal
                onCancel={onCancelPreregisterModal}
                setBusinessDetails={setBusinessDetails}
                selectedBusiness={business}
                setPreSignupHeader={setPreSignupHeader}
                setIsPreRegisterationSuccessfull={
                  setIsPreRegisterationSuccessfull
                }
              />
            </ModalComponent>
          </Render>
        </Render>
        <Render when={!!activateOfferModal}>
          <ModalComponent
            showClose
            containerClass="md:w-[50vw] md:h-auto h-auto  max-h-[80vh] w-[80vw] !md:m-0 !p-0 !z-50"
            onClose={onCancelActivateOfferModal}
            renderHeader={
              <div className="text-darkBlue md:text-xl text-base font-medium">
                Activate Plan
              </div>
            }
          >
            <ActivateOfferModal
              activateOfferModal={activateOfferModal}
              onSuccess={onCancelActivateOfferModal}
              onClose={onCloseModal}
              setErrorMessage={(value) => setErrorMessage(value)}
            />
          </ModalComponent>
        </Render>
        <Render when={!!isPaymentSucecss}>
          <ModalComponent
            showClose
            containerClass="md:w-[420px] md:h-[320px] h-[320px] w-[320px] px-4 pt-2"
            onClose={onPaymentSucecssModalClose}
          >
            <PaymentSucecssModal onClose={onPaymentSucecssModalClose} />
          </ModalComponent>
        </Render>
      </div>
    </>
  );
};

export default Home;
